import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';

import { A11yModule } from '@angular/cdk/a11y';
import { Geofence } from 'src/app/models/geofence';
import { SolutionSpace } from 'src/app/models/solution';
import { ComponentType } from 'src/app/models/component-type';
import { ComponentTypeService } from 'src/app/services/api/componentType.service';
import { GeofenceService } from 'src/app/services/api/geofence.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { SelectorDialogComponent } from 'src/app/shared/selector-dialog/selector-dialog.component';

export interface ServiceLocationDialogData {
  geofence: Geofence;
  solutionSpace: SolutionSpace;
}

@Component({
  selector: 'app-service-location-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatListModule, MatTooltipModule, A11yModule],
  templateUrl: './service-location-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./service-location-dialog.component.scss'],
})
export class ServiceLocationDialogComponent implements OnInit {
  componentTypes: ComponentType[] = [];
  allComponentTypes: ComponentType[] = [];
  isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ServiceLocationDialogData,
    private dialogRef: MatDialogRef<ServiceLocationDialogComponent>,
    private componentTypeService: ComponentTypeService,
    private geofenceService: GeofenceService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadComponentTypes();
    this.loadAllComponentTypes();
  }

  loadComponentTypes(): void {
    this.isLoading = true;
    this.geofenceService.getComponentTypesForGeofence(this.data.geofence.id).subscribe({
      next: (types) => {
        this.componentTypes = types;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading component types:', error);
        this.snackbarService.openSnackBar({ title: 'Error', message: 'Failed to load component types' }, 'ERROR');
        this.isLoading = false;
      },
    });
  }

  loadAllComponentTypes(): void {
    this.componentTypeService.getComponentTypes(this.data.solutionSpace, { pageSize: 10000 }).subscribe({
      next: (response) => {
        this.allComponentTypes = response.items;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading all component types:', error);
      },
    });
  }

  removeComponentType(componentType: ComponentType): void {
    this.componentTypeService.removeGeofenceFromComponentType(componentType.id, this.data.geofence.id).subscribe({
      next: () => {
        this.snackbarService.openSnackBar(
          { title: 'Success', message: 'Component type removed from service location' },
          'UPDATE'
        );
        this.loadComponentTypes();
      },
      error: (error: HttpErrorResponse) => {
        this.snackbarService.openSnackBar({ title: 'Error', message: 'Failed to remove component type' }, 'ERROR');
      },
    });
  }

  addComponentType(): void {
    const availableTypes = this.allComponentTypes.filter(
      (type) => !this.componentTypes.some((ct) => ct.id === type.id)
    );

    if (availableTypes.length === 0) {
      this.snackbarService.openSnackBar({ title: 'Info', message: 'All component types are already added' }, 'UPDATE');
      return;
    }

    const dialogRef = this.dialog.open(SelectorDialogComponent<ComponentType>, {
      width: '500px',
      data: {
        title: 'Add Component Type',
        description: 'Select a component type to add to this service location',
        label: 'Component Type',
        items: availableTypes,
        displayFn: (type: ComponentType) => type.name,
        primaryButtonText: 'Add',
      },
    });

    dialogRef.afterClosed().subscribe((selectedTypes: ComponentType[] | undefined) => {
      if (selectedTypes && selectedTypes.length > 0) {
        const requests = selectedTypes.map((type) =>
          this.componentTypeService.addGeofenceToComponentType(type.id, this.data.geofence.id)
        );
        forkJoin(requests).subscribe({
          next: () => {
            this.snackbarService.openSnackBar(
              {
                title: 'Success',
                message:
                  selectedTypes.length === 1
                    ? 'Component type added to service location'
                    : `${selectedTypes.length} component types added to service location`,
              },
              'UPDATE'
            );
            this.loadComponentTypes();
          },
          error: (error: HttpErrorResponse) => {
            this.snackbarService.openSnackBar({ title: 'Error', message: 'Failed to add component type' }, 'ERROR');
          },
        });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
